import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  MenuItem,
  Select,
  Collapse,
} from '@mui/material';
import SingleDatePicker from '../date-picker/SingleDatePicker';
import { X, ChevronDown, ChevronRight } from 'lucide-react';
import { PatientFormData } from '../../types/patient';
import { createPatient, resetErrors, FieldErrors } from '../../redux/slices/patientsSlice';
import { RootState } from '../../redux/store';
import { getFieldError } from '../../utils/errorParser';
import { isValidEmail } from '../../pages/Auth/utils';

interface AddPatientModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit?: () => void;
  loading?: boolean;
}

const AddPatientModal: React.FC<AddPatientModalProps> = ({
  open,
  onClose,
  onSubmit,
  loading = false,
}) => {
  const dispatch = useDispatch();
  const { createPatient: createPatientRedux } = useSelector((state: RootState) => state.patients);
  const isLoading = createPatientRedux.loading || loading;

  const [formData, setFormData] = useState<PatientFormData>({
    firstName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    email: '',
    phone: '+61 ',
    address: '',
    medicareNumber: '',
    city: '',
    state: '',
    zipCode: '',
    primaryCondition: '',
    allergies: '',
    medications: '',
    previousTreatments: '',
    medicalHistory: '',
    emergencyContactName: '',
    emergencyContactRelation: '',
    emergencyContactPhone: '',
    insuranceProvider: '',
    insuranceNumber: '',
    insuranceGroup: '',
    occupation: '',
    referredBy: '',
    preferredLanguage: 'EN', // Hardcoded in API
    communicationPreference: 'EMAIL', // Hardcoded in API
  });

  const [errors, setErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors | undefined>(undefined);
  const [touchedFields, setTouchedFields] = useState<Set<string>>(new Set());
  const [emergencyContactExpanded, setEmergencyContactExpanded] = useState(false);

  useEffect(() => {
    if (open) {
      dispatch(resetErrors());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (createPatientRedux.error) {
      const errorObj = createPatientRedux.error;

      // Store field errors for inline display
      setFieldErrors(errorObj.fieldErrors);

      // Set the main error message
      if (errorObj.message) {
        setErrors([errorObj.message]);
      } else {
        setErrors(['An error occurred while creating the patient.']);
      }
    }
  }, [createPatientRedux.error]);

  const handleInputChange = (field: keyof PatientFormData, value: string) => {
    let processedValue = value;

    // Format phone number if it's the phone field
    if (field === 'phone') {
      // If user tries to delete the +61 prefix or input is empty, restore it
      if (value.trim() === '' || (!value.startsWith('+61') && value.length < 4)) {
        processedValue = '+61 ';
      } else if (value.startsWith('+61')) {
        // If it already has +61, just clean up the digits after it
        const digitsAfterPrefix = value.replace(/^\+61\s*/, '').replace(/\D/g, '');
        processedValue = '+61 ' + digitsAfterPrefix;
      } else {
        // If it doesn't start with +61 but has content, extract digits and add prefix
        const digits = value.replace(/\D/g, '');
        processedValue = '+61 ' + digits;
      }
    }

    setFormData(prev => ({
      ...prev,
      [field]: processedValue,
    }));
    // Mark field as touched
    setTouchedFields(prev => new Set([...prev, field]));

    // Clear any existing errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
      setFieldErrors(undefined);
    }
  };

  const isFormValid = (): boolean => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      isValidEmail(formData.email) &&
      (!formData.dateOfBirth || validateDateOfBirth(formData.dateOfBirth))
    );
  };



  // Date of birth validation function
  const validateDateOfBirth = (dateString: string): boolean => {
    if (!dateString) return true; // Optional field

    try {
      const date = new Date(dateString);
      const now = new Date();
      const minDate = new Date('1900-01-01');

      // Check if date is valid
      if (isNaN(date.getTime())) return false;

      // Check if date is in reasonable range
      if (date > now || date < minDate) return false;

      // Check if person would be older than 150 years
      const age = now.getFullYear() - date.getFullYear();
      if (age > 150) return false;

      return true;
    } catch {
      return false;
    }
  };

  // Australian phone number validation
  const validateAustralianPhone = (phone: string): boolean => {
    // Handle international format (+61)
    if (phone.startsWith('+61')) {
      // Remove +61 and get the remaining digits
      const digitsAfterCountryCode = phone.replace(/^\+61\s*/, '').replace(/\D/g, '');

      // Mobile: 4XX XXX XXX (9 digits starting with 4) - when entered as +61 4XX XXX XXX
      // Landline: X XXXX XXXX (9 digits starting with 2, 3, 7, 8) - when entered as +61 X XXXX XXXX
      // Mobile: 04XX XXX XXX (10 digits starting with 04) - when entered as +61 04XX XXX XXX
      // Landline: 0X XXXX XXXX (10 digits starting with 02, 03, 07, 08) - when entered as +61 0X XXXX XXXX

      const mobilePattern9 = /^4\d{8}$/; // 4 + 8 digits (standard international format)
      const landlinePattern9 = /^[2378]\d{8}$/; // 2, 3, 7, 8 + 8 digits (standard international format)
      const mobilePattern10 = /^04\d{8}$/; // 04 + 8 digits (local format with +61 prefix)
      const landlinePattern10 = /^0[2378]\d{8}$/; // 02, 03, 07, 08 + 8 digits (local format with +61 prefix)

      return (
        mobilePattern9.test(digitsAfterCountryCode) ||
        landlinePattern9.test(digitsAfterCountryCode) ||
        mobilePattern10.test(digitsAfterCountryCode) ||
        landlinePattern10.test(digitsAfterCountryCode)
      );
    }

    // Handle local format (0X)
    const cleanPhone = phone.replace(/\D/g, '');

    // Mobile: 04XX XXX XXX (10 digits starting with 04)
    // Landline: 02XXXX XXXX, 03XXXX XXXX, 07XXXX XXXX, 08XXXX XXXX (10 digits starting with 02, 03, 07, 08)
    const mobilePattern = /^04\d{8}$/; // 04 + 8 digits
    const landlinePattern = /^0[2378]\d{8}$/; // 02, 03, 07, 08 + 8 digits

    return mobilePattern.test(cleanPhone) || landlinePattern.test(cleanPhone);
  };

  const validateForm = (): boolean => {
    const newErrors: string[] = [];

    if (!formData.firstName.trim()) {
      newErrors.push('First name is required');
    }
    if (!formData.lastName.trim()) {
      newErrors.push('Last name is required');
    }
    if (!formData.email.trim()) {
      newErrors.push('Email is required');
    } else if (!isValidEmail(formData.email)) {
      newErrors.push('Please enter a valid email address');
    }
    console.log('Validating phone number:', formData.phone);
    if (formData.phone && formData.phone.trim() !== '+61' && !validateAustralianPhone(formData.phone)) {
      newErrors.push('Phone number is invalid - please enter a valid Australian phone number');
    }
    if (formData.dateOfBirth && !validateDateOfBirth(formData.dateOfBirth)) {
      newErrors.push('Please enter a valid date of birth');
    }

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleSubmit = () => {
    // Clear any existing errors before validation
    setErrors([]);
    setFieldErrors(undefined);

    if (validateForm()) {
      const formDataToSend = {
        ...formData,
        date_of_birth: formData.dateOfBirth ? formData.dateOfBirth : null,
      };
      dispatch(
        createPatient({
          data: formDataToSend,
          cb: () => {
            handleClose();
            if (onSubmit) {
              onSubmit();
            }
          },
        })
      );
    }
  };

  const handleClose = () => {
    setFormData({
      firstName: '',
      lastName: '',
      dateOfBirth: '',
      gender: '',
      email: '',
      phone: '+61 ',
      address: '',
      medicareNumber: '',
      city: '',
      state: '',
      zipCode: '',
      primaryCondition: '',
      allergies: '',
      medications: '',
      previousTreatments: '',
      medicalHistory: '',
      emergencyContactName: '',
      emergencyContactRelation: '',
      emergencyContactPhone: '',
      insuranceProvider: '',
      insuranceNumber: '',
      insuranceGroup: '',
      occupation: '',
      referredBy: '',
      preferredLanguage: 'EN', // Hardcoded in API
      communicationPreference: 'EMAIL', // Hardcoded in API
    });
    setErrors([]);
    setFieldErrors(undefined);
    dispatch(resetErrors());
    setTouchedFields(new Set());
    setEmergencyContactExpanded(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          minHeight: '600px',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, marginBottom: '24px', borderBottom: '1px solid #E0E0E0' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" fontWeight={500}>
            Add new patient
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <X size={20} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 3 }}>
        {errors.length > 0 && (
          <Box
            role="alert"
            aria-live="polite"
            sx={{
              mb: 2,
              p: 2,
              bgcolor: '#FEF2F2',
              border: '1px solid #FCA5A5',
              borderRadius: 1,
            }}
          >
            {errors.length === 1 ? (
              <Typography variant="body2" color="#DC2626" fontWeight={500}>
                {errors[0]}
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="#DC2626" fontWeight={500} mb={1}>
                  Please fix the following errors:
                </Typography>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {errors.map((error, index) => (
                    <Typography
                      key={index}
                      component="li"
                      variant="body2"
                      color="#DC2626"
                      sx={{ mb: 0.5 }}
                    >
                      {error}
                    </Typography>
                  ))}
                </Box>
              </>
            )}
          </Box>
        )}
        <Box display="flex" flexDirection="column" gap={3}>
          {/* Personal Information Section */}
          <Box>
            <Typography fontSize={'1.25rem'} fontWeight={600} mb={2}>
              Personal information
            </Typography>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Box>
                <Typography
                  fontSize={'0.875rem'}
                  fontWeight={600}
                  mb={'6px'}
                  color={
                    touchedFields.has('firstName') && !formData.firstName.trim()
                      ? 'error.main'
                      : 'text.primary'
                  }
                >
                  First name<span style={{ color: '#d32f2f' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Add first name"
                  value={formData.firstName}
                  onChange={e => handleInputChange('firstName', e.target.value)}
                  size="small"
                  error={!!getFieldError(fieldErrors, 'first_name')}
                  helperText={getFieldError(fieldErrors, 'first_name')}
                />
              </Box>
              <Box>
                <Typography
                  fontSize={'0.875rem'}
                  fontWeight={600}
                  mb={'6px'}
                  color={
                    touchedFields.has('lastName') && !formData.lastName.trim()
                      ? 'error.main'
                      : 'text.primary'
                  }
                >
                  Last name<span style={{ color: '#d32f2f' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Add last name"
                  value={formData.lastName}
                  onChange={e => handleInputChange('lastName', e.target.value)}
                  size="small"
                  error={!!getFieldError(fieldErrors, 'last_name')}
                  helperText={getFieldError(fieldErrors, 'last_name')}
                />
              </Box>
            </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={2}>
              <Box>
                <Typography
                  fontSize={'0.875rem'}
                  fontWeight={600}
                  mb={'6px'}
                  color={
                    touchedFields.has('dateOfBirth') &&
                    formData.dateOfBirth &&
                    !validateDateOfBirth(formData.dateOfBirth)
                      ? 'error.main'
                      : 'text.primary'
                  }
                >
                  Date of birth
                </Typography>
                <SingleDatePicker
                  value={formData.dateOfBirth ? new Date(formData.dateOfBirth) : null}
                  onChange={newValue => {
                    if (newValue) {
                      const formattedDate = newValue.toISOString().split('T')[0];
                      handleInputChange('dateOfBirth', formattedDate);
                    } else {
                      handleInputChange('dateOfBirth', '');
                    }
                  }}
                  maxDate={new Date()}
                  minDate={new Date('1900-01-01')}
                  placeholder="DD/MM/YYYY"
                />
              </Box>
              <Box>
                <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                  Medicare number
                </Typography>
                <TextField
                  fullWidth
                  placeholder="1234 53789 0"
                  value={formData.medicareNumber}
                  onChange={e => handleInputChange('medicareNumber', e.target.value)}
                  size="small"
                />
              </Box>
            </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={2}>
              <Box>
                <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                  Phone
                </Typography>
                <TextField
                  fullWidth
                  placeholder="e.g., 412 345 678 or 2 9876 5432"
                  value={formData.phone}
                  onChange={e => handleInputChange('phone', e.target.value)}
                  size="small"
                  error={!!getFieldError(fieldErrors, 'phone')}
                  helperText={getFieldError(fieldErrors, 'phone')}
                />
              </Box>
              <Box>
                <Typography
                  fontSize={'0.875rem'}
                  fontWeight={600}
                  mb={'6px'}
                  color={
                    touchedFields.has('email') &&
                    (!formData.email.trim() || !isValidEmail(formData.email))
                      ? 'error.main'
                      : 'text.primary'
                  }
                >
                  Email<span style={{ color: '#d32f2f' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="jane.doe@example.com"
                  value={formData.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  size="small"
                  error={
                    !!getFieldError(fieldErrors, 'email') ||
                    (touchedFields.has('email') &&
                      formData.email.trim() !== '' &&
                      !isValidEmail(formData.email))
                  }
                  helperText={
                    getFieldError(fieldErrors, 'email') ||
                    (touchedFields.has('email') &&
                    formData.email.trim() !== '' &&
                    !isValidEmail(formData.email)
                      ? 'Please enter a valid email address'
                      : '')
                  }
                />
              </Box>
            </Box>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mt={2}>
              <Box>
                <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                  Gender
                </Typography>
                <Select
                  value={formData.gender}
                  onChange={e => handleInputChange('gender', e.target.value)}
                  displayEmpty
                  renderValue={value => value || 'Select gender'}
                  size="small"
                  fullWidth
                >
                  <MenuItem value="">Select gender</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                  <MenuItem value="prefer_not_to_say">Prefer not to say</MenuItem>
                </Select>
              </Box>
              <Box>
                <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                  Occupation
                </Typography>
                <TextField
                  fullWidth
                  placeholder="e.g., Teacher, Engineer"
                  value={formData.occupation}
                  onChange={e => handleInputChange('occupation', e.target.value)}
                  size="small"
                />
              </Box>
            </Box>
            <Box mt={2}>
              <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                Address
              </Typography>
              <TextField
                fullWidth
                placeholder="Add non-medical preferences, e.g. 'prefers printed handouts'"
                value={formData.address}
                onChange={e => handleInputChange('address', e.target.value)}
                size="small"
                error={!!getFieldError(fieldErrors, 'address')}
                helperText={getFieldError(fieldErrors, 'address')}
              />
            </Box>
            <Box mt={2}>
              <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                Notes
              </Typography>
              <TextField
                fullWidth
                placeholder="Add non-medical preferences, e.g. prefers printed handouts"
                value={formData.medicalHistory}
                onChange={e => handleInputChange('medicalHistory', e.target.value)}
                rows={1}
              />
            </Box>
          </Box>

          {/* Medical Condition Section - Collapsible */}
          <Box>
            <Typography fontSize={'1.25rem'} fontWeight={600} mb={2}>
              Medical condition
            </Typography>
            <Box>
              <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                Medical condition
              </Typography>
              <TextField
                fullWidth
                placeholder="Describe the patient's medical condition"
                value={formData.primaryCondition}
                onChange={e => handleInputChange('primaryCondition', e.target.value)}
                multiline
                rows={3}
                size="small"
                inputProps={{ maxLength: 500 }}
                helperText={`${formData.primaryCondition.length}/500 characters`}
              />
            </Box>
          </Box>

          {/* Emergency Contact Section - Collapsible */}
          <Box>
            <Box
              onClick={() => setEmergencyContactExpanded(!emergencyContactExpanded)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                '&:hover': { opacity: 0.8 },
                justifyContent: 'space-between',
              }}
            >
              <Typography fontSize={'1.25rem'} fontWeight={600} mb={2}>
                Emergency contact
              </Typography>
              {emergencyContactExpanded ? (
                <ChevronDown size={28} style={{ marginLeft: '8px' }} />
              ) : (
                <ChevronRight size={28} style={{ marginLeft: '8px' }} />
              )}
            </Box>
            <Collapse in={emergencyContactExpanded}>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                <Box>
                  <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                    Name
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="Enter contact name"
                    value={formData.emergencyContactName}
                    onChange={e => handleInputChange('emergencyContactName', e.target.value)}
                    size="small"
                  />
                </Box>
                <Box>
                  <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                    Relationship
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder="e.g., Spouse, Parent, Sibling"
                    value={formData.emergencyContactRelation}
                    onChange={e => handleInputChange('emergencyContactRelation', e.target.value)}
                    size="small"
                  />
                </Box>
              </Box>
              <Box mt={2}>
                <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                  Phone
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Enter contact phone number"
                  value={formData.emergencyContactPhone}
                  onChange={e => handleInputChange('emergencyContactPhone', e.target.value)}
                  size="small"
                />
              </Box>
              <Box mt={2}>
                <Typography fontSize={'0.875rem'} fontWeight={600} mb={'6px'}>
                  Notes
                </Typography>
                <TextField
                  fullWidth
                  placeholder="e.g., Best to call in evenings"
                  value={formData.medicalHistory}
                  onChange={e => handleInputChange('medicalHistory', e.target.value)}
                  size="small"
                />
              </Box>
            </Collapse>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isLoading || !isFormValid()}
          sx={{
            bgcolor: '#7B61FF',
            '&:hover': { bgcolor: '#6A4FEF' },
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
          }}
        >
          {isLoading ? 'Saving...' : 'Create new patient'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddPatientModal;
